import {
  Button,
  Card,
  Dialog,
  ProgressSpinner,
  Typography,
} from '@sl/react-datafabric-components';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { classNames } from 'primereact/utils';
import { useMemo, useState } from 'react';
import { useAgentContext } from '../../../../../common/context/agent.provider';
import { useUpdateSubscriptionMutation } from '../../../../../common/redux/services/subscriptions';
import { useGetTransactionsQuery } from '../../../../../common/redux/services/transactions';
import { BuyTokensDialog } from '../../manage-subscription/buy-tokens-success-dialog';
import TransactionsDialog from './transactions-dialog';

export default function CreditsPage({
  showRecentTransactions = true,
}: {
  showRecentTransactions?: boolean;
}) {
  const [showTransactionsDialog, setShowTransactionsDialog] = useState(false);
  const [showBuyTokensDialog, setShowBuyTokensDialog] = useState(false);
  const {
    agentSubscriptions: subscriptions = [],
    refetchSubscriptions,
    loadingSubscriptions,
  } = useAgentContext();
  const { data: transactions, isLoading } = useGetTransactionsQuery(
    {
      page: 1,
      pageSize: 8,
    },
    { skip: !showRecentTransactions }
  );
  const [updateSubscription, { isLoading: loadingUpdateSubscription }] =
    useUpdateSubscriptionMutation();

  const handleAddCredits = () => {
    setShowBuyTokensDialog(true);
  };

  const subscription = subscriptions?.find((sub) => sub.status === 'active');

  const [isEditingAutoTopup, setIsEditingAutoTopup] = useState(false);
  const [isAutoTopupEnabled, setIsAutoTopupEnabled] = useState<boolean>(
    Boolean(subscription?.autoTopUp) || false
  );
  const [autoTopupAmount, setAutoTopupAmount] = useState<number>(
    subscription?.autoTopUpValue || 1
  );

  const handleSaveAutoTopup = () => {
    updateSubscription({
      id: subscription?.id,
      autoTopUp: isAutoTopupEnabled,
      autoTopUpValue: autoTopupAmount,
    });
    setIsEditingAutoTopup(false);
  };

  const credits = useMemo(
    () =>
      subscription
        ? (subscription?.currentTokens || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : 'N/A',
    [subscription]
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  return (
    <>
      <Typography
        type={3}
        className='font-bold border-bottom-1 pb-4 mb-4 surface-border'
      >
        Credits
      </Typography>

      {/* Current Balance */}
      <Card
        className='mb-4 surface-ground'
        pt={{ content: { className: 'p-0' } }}
      >
        <div className='flex align-items-center justify-content-between'>
          <Typography type={2} className='font-bold text-800 m-0'>
            {credits}
          </Typography>
          {loadingSubscriptions ? (
            <ProgressSpinner className='w-1rem h-1rem m-0 mr-1' />
          ) : (
            <i
              className='pi pi-refresh text-500 hover:text-700 cursor-pointer'
              style={{ fontSize: '1.2rem' }}
              onClick={() => refetchSubscriptions()}
            />
          )}
        </div>
      </Card>

      {/* Credit Management Cards */}
      <div className='grid'>
        {/* Buy Credits Card */}
        <div className='col-12 md:col-6'>
          <Card className='h-full shadow-5'>
            <div className='flex justify-content-between align-items-center mb-3'>
              <Typography type={5} className='font-bold'>
                Buy Credits
              </Typography>
            </div>

            <Button
              label='Add Credits'
              className='w-full mt-2'
              onClick={handleAddCredits}
            />
          </Card>
        </div>

        {/* Auto Top-Up Card */}
        <div className='col-12 md:col-6'>
          <Card className='h-full shadow-5'>
            <div className='flex justify-content-between align-items-center mb-3'>
              <Typography type={5} className='font-bold'>
                Auto Top-Up
              </Typography>
              <div className='flex align-items-center gap-2 mb-3'>
                <Typography className='text-sm m-0'>
                  {isAutoTopupEnabled ? 'Enabled' : 'Disabled'}
                </Typography>
                <InputSwitch
                  checked={isAutoTopupEnabled}
                  disabled={isEditingAutoTopup}
                  onChange={(e) => {
                    if (e.value) {
                      setIsEditingAutoTopup(true);
                    }
                    setIsAutoTopupEnabled(e.value || false);
                  }}
                />
              </div>
            </div>

            {/* Summary state when enabled and not editing */}
            {isAutoTopupEnabled && !isEditingAutoTopup && (
              <div className='flex flex-column gap-2'>
                <Typography className='text-600'>
                  Auto top-up is <b>enabled</b> and will add
                  <b> {autoTopupAmount}</b> credits when your balance drops
                  below
                  <b> 20.</b>
                  <i
                    className={classNames(
                      `pi pi-pencil cursor-pointer text-primary hover:text-800 ml-2`
                    )}
                    style={{ fontSize: '0.9rem' }}
                    onClick={() => setIsEditingAutoTopup(true)}
                  />
                </Typography>
              </div>
            )}

            {/* Setup/Edit state */}
            {isAutoTopupEnabled && isEditingAutoTopup && (
              <div className='flex flex-column gap-1'>
                <Typography className='text-600'>
                  Automatically add{' '}
                  <InputNumber
                    value={autoTopupAmount}
                    onChange={(e) => setAutoTopupAmount(e.value)}
                    min={1}
                    pt={{ input: { root: { className: 'w-5rem h-2rem' } } }}
                  />{' '}
                  credits when your balance is below <b> 20</b>.
                  {loadingUpdateSubscription ? (
                    <ProgressSpinner
                      className='ml-2'
                      style={{ width: '0.9rem', height: '0.9rem' }}
                    />
                  ) : (
                    <i
                      className={classNames(
                        `pi pi-check cursor-pointer text-primary hover:text-800 ml-2`
                      )}
                      style={{ fontSize: '0.9rem' }}
                      onClick={() => handleSaveAutoTopup()}
                    />
                  )}
                </Typography>
              </div>
            )}

            {!isAutoTopupEnabled && (
              <div className='flex flex-column gap-1'>
                <Typography className='text-600 mb-1'>
                  Auto top-up is <b>disabled</b>.
                </Typography>
                <Typography className='text-400 text-xs'>
                  Automatically add credits when your balance is below{' '}
                  <b> 20</b>.
                </Typography>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Recent Transactions */}
      {showRecentTransactions && (
        <div className='m-4 mt-6'>
          <div className='flex justify-content-between align-items-center mb-4'>
            <Typography type={5} className='font-bold'>
              Recent Transactions
            </Typography>
            <div
              className='flex align-items-center gap-2 text-primary cursor-pointer'
              onClick={() => setShowTransactionsDialog(true)}
            >
              <Typography className='text-sm mb-0'>Payment History</Typography>
              <i className='pi pi-external-link text-xs' />
            </div>
          </div>

          {isLoading ? (
            <div className='text-center py-4'>
              <i className='pi pi-spin pi-spinner text-2xl text-600' />
              <Typography className='text-600 mt-2'>
                Loading transactions...
              </Typography>
            </div>
          ) : transactions?.data?.length > 0 ? (
            <div className='space-y-3'>
              {transactions.data.map((transaction: any, index: number) => (
                <div
                  key={transaction.id || index}
                  className='flex justify-content-between align-items-center surface-border'
                >
                  <Typography className='font-medium'>
                    {formatDate(transaction.createdAt || transaction.date)}
                  </Typography>

                  <Typography className='font-bold'>
                    {transaction.amount || 100}
                  </Typography>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-4'>
              <i className='pi pi-receipt text-4xl text-400 mb-3' />
              <Typography className='text-600'>
                No transactions found
              </Typography>
            </div>
          )}
        </div>
      )}

      {/* Transactions Dialog */}
      <Dialog
        visible={showTransactionsDialog}
        onHide={() => setShowTransactionsDialog(false)}
        style={{ width: '50vw' }}
      >
        <TransactionsDialog />
      </Dialog>

      {/* Buy Tokens Dialog */}
      <BuyTokensDialog
        visible={showBuyTokensDialog}
        onHide={() => setShowBuyTokensDialog(false)}
        subscriptionData={subscription}
      />
    </>
  );
}
